import {
  Box,
  Button,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import { API_DELETE, API_PATCH, DBRequest, useConfig } from '../../api/api';
import { ProjectAddElementButton } from './project/ProjectAddElementButton';
import ExoDialog from './ExoDialog';
import { NewTask } from '../new/NewTask';
import { ExoSkeleton } from './ExoSkeleton';
import DateLabel from '../label/DateLabel';
import { TimeLabel } from '../label/TimeLabel';
import EditIcon from '@mui/icons-material/Edit';
import { updateOrCreate } from '../special/updateOrCreate';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RenderHTML from '../preview/RenderHTML';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import DeleteIcon from '@mui/icons-material/Delete';
import ExoAlert from './ExoAlert';

export const ExoTasks = ({
  tasks,
  toggelSx,
  noNew,
  targetType,
  targetId,
  onEdit,
  onChange,
  onStatusChange,
  onFeedback,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const [showStatus, setShowStatus] = useState('0');
  const [openNewTask, setOpenNewTask] = useState(false);
  const [reqDel, setReqDel] = useState(null);

  function handleDelete(element) {
    setReqDel(null);
    DBRequest({
      config,
      path: `user-tasks/${element.id}`,
      method: API_DELETE,
      onResponse: () => {
        const newTasks = tasks.data.filter((obj) => obj.id !== element.id);
        onChange({ ...tasks, data: newTasks });
      },
      onFeedback,
    });
  }

  // task change
  function updateTasks(res) {
    const newTasks = updateOrCreate(tasks, res.data);
    onChange(newTasks);
  }

  return (
    <Box>
      <ToggleButtonGroup
        sx={{
          ...toggelSx,
          '& button': {
            padding: '3px 8px',
          },
        }}
        size="small"
        value={showStatus}
        exclusive
        onChange={(e) => {
          onStatusChange(e.target.value);
          setShowStatus(e.target.value);
          onChange(null);
        }}
        aria-label="Task Status"
      >
        <ToggleButton value={'0'}>
          <PanoramaFishEyeIcon sx={{ fontSize: '12px' }} className="mr-1" />
          {t('Open')}
        </ToggleButton>
        <ToggleButton value={'1'}>
          <TaskAltIcon sx={{ fontSize: '12px' }} className="mr-1" />
          {t('Finished')}
        </ToggleButton>
      </ToggleButtonGroup>
      {!noNew && (
        <Box className="flex flex-col gap-1 pt-2">
          <ProjectAddElementButton
            sizePx="20px"
            size="small"
            label={t('add task')}
          />
        </Box>
      )}
      <Box className="flex flex-col gap-1">
        {tasks ? (
          tasks?.data.map((element) => (
            <Task
              key={element.id}
              data={element}
              onEdit={() => onEdit(element)}
              onChange={updateTasks}
              onDelete={() => setReqDel(element)}
            />
          ))
        ) : (
          <ExoSkeleton n={6} />
        )}
      </Box>
      <ExoAlert
        onSubmit={() => handleDelete(reqDel)}
        open={Boolean(reqDel)}
        onClose={() => setReqDel(null)}
        alert={{
          submitLabel: t('Delete'),
          message: t('Are you sure you want to permanently delete this entry?'),
        }}
      />
      <ExoDialog
        open={openNewTask}
        onClose={() => setOpenNewTask(false)}
        limitWidth
      >
        <NewTask
          onClose={() => setOpenNewTask(false)}
          targetType={targetType}
          targetId={targetId}
        />
      </ExoDialog>
    </Box>
  );
};

const Task = ({ data, onEdit, onChange, onDelete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [openDiscription, setOpenDiscription] = useState(false);

  const { name, deadline, description, reminders } = data;

  function handleStatusChange() {
    DBRequest({
      config,
      path: `user-tasks/${data.id}`,
      method: API_PATCH({ status: data.status ? 0 : 1 }),
      onResponse: onChange,
      onLoading: setIsLoading,
    });
  }

  const Icon = data.status ? TaskAltIcon : PanoramaFishEyeIcon;

  return (
    <Box
      className="relative"
      sx={{
        bgcolor: colors.card,
        borderRadius: '10px',
        padding: '6px 8px',
        paddingBottom: deadline ? '20px' : '',
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        '& .editButton': {
          transition: '0.3s ease',
          bgcolor: colors.success + '30',
          color: colors.success,
          border: `1px solid ${colors.success}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
    >
      <Box className="flex flex-row gap-2">
        {isLoading ? (
          <span
            className="loader"
            style={{
              width: '25px',
              height: '25px',
            }}
          ></span>
        ) : (
          <Button
            onClick={handleStatusChange}
            sx={{
              padding: '0px!important',
              width: '25px',
              height: '25px',
              minWidth: 'unset',
              borderRadius: '9999px',
            }}
          >
            <Icon sx={{ fontSize: '20px' }} />
          </Button>
        )}
        <Box className="flex flex-col w-full">
          <Box className="flex items-center justify-between gap-2">
            <Box className="flex gap-1 items-center w-full justify-between">
              <Typography fontSize="14px" textAlign="left" fontWeight={600}>
                {name}
              </Typography>
              <Box className="flex gap-1">
                <IconButton
                  onClick={onEdit}
                  size="small"
                  className="editButton"
                >
                  <EditIcon sx={{ fontSize: '15px' }} />
                </IconButton>
                <IconButton
                  onClick={onDelete}
                  size="small"
                  className="delButton"
                >
                  <DeleteIcon sx={{ fontSize: '15px' }} />
                </IconButton>
              </Box>
            </Box>
            {reminders?.length > 0 && (
              <Button
                sx={{
                  padding: '0px!important',
                  minWidth: '25px',
                  minHeight: '25px',
                  borderRadius: '9999px',
                  bgcolor: colors.warning + '30',
                }}
                className="flex items-center justify-center"
              >
                <AccessAlarmIcon
                  sx={{ fontSize: '16px', color: colors.warning }}
                />
              </Button>
            )}
          </Box>

          {description && (
            <Box className="flex flex-col gap-1">
              <Button
                sx={{ padding: 0, justifyContent: 'space-between' }}
                onClick={() => setOpenDiscription(!openDiscription)}
                className="flex items-center justify-between"
              >
                <Typography
                  fontWeight={500}
                  fontSize="12px"
                  className="opacity-60"
                >
                  {openDiscription ? t('show less...') : t('show more...')}
                </Typography>
                <ArrowDropDownIcon
                  sx={{
                    transform: `rotate(${openDiscription ? 180 : 0}deg)`,
                  }}
                />
              </Button>
              <Collapse in={openDiscription}>
                <RenderHTML HTML={description} emptyHTML={' '} />
              </Collapse>
            </Box>
          )}
        </Box>
      </Box>

      {deadline && (
        <Typography
          fontSize="11px"
          className="opacity-50 absolute bottom-1 right-2"
        >
          <DateLabel date={deadline} /> -{' '}
          <TimeLabel time={deadline} format="HH:mm" />
        </Typography>
      )}
    </Box>
  );
};
